// THE AGE IS READ OFF THE BIRTH DATE (TW-0503, 2026-10-29). The form asked for a typed age AND a birth date, and 117 signed
// rows disagreed with themselves (docs/FORM-AUDIT-2026-10-26.md § 5): a typed "15" beside a birth date that made the client 14
// signed five minors' piercings with no parent on them, and the refusal that held the door since (`birth_date_disagrees`) asked
// about 2 clients a day to fix a typo. One source per fact: the age IS the birth date on the day of signing, so the age box is
// gone, the page shows the age it reads ("You are 24.", Form.tsx), and every minors route reads that number.
//
// THIS FILE AND services/waiver-api/consolidated_api/birth_date.py ARE ONE FUNCTION IN TWO LANGUAGES. The signing door reads
// the age off the posted birth date the same way and never trusts a posted age; both are held to the same table of days,
// birthDate.cases.json beside this file, by birthDate.test.ts here and tests/unit/test_birth_date.py there, so a change to one
// that the other does not make goes red on both sides.
//
// THE RULES, each the same on both sides:
//   * A box holds ASCII digits and nothing else (spaces and tabs at either end aside), or there is no birth date.
//   * A year under 100 is two digits typed for four: 20yy when that birth date is not after the day of signing, else 19yy.
//     "95" is 1995 (2095 has not happened), "05" is 2005, "10" is 2010. The page shows the age it read, so a client whose
//     two digits meant the other century sees it and types four.
//   * The month, the day and the year make a day on the calendar (no April 31, a February 29 only in a leap year), and that
//     day is not after the day of signing, or there is no birth date.
//   * The age is whole years: a birthday not yet reached in the year of signing is a year younger. A February 29 birthday is
//     reached on February 28 in a common year, which is the reading the page made before this (luxon's), so nobody it
//     signed as 18 on that day is read as 17 now.
//
// THE DAY OF SIGNING is the signed date's own box, the date the record prints beside the birth date, so the record's age
// agrees with the record's two dates and anyone reading the paper can work it out again. The page fills that box with the
// phone's own today, the shop's day where the client stands at the counter, and keeps it today until the client changes
// it (a page open past midnight moves to the new day with it: Form.tsx). A signed date that is not a day, or is later than
// the latest day it is anywhere on Earth at this instant, cannot be the day of signing, and today is used: the phone's here,
// the shop's zone at the door. So a client can never make themselves older by dating the signing ahead.

/** A day on the calendar: no clock, no zone. `month` and `day` count from 1. */
export type CalendarDay = { year: number; month: number; day: number };

/** The zone furthest ahead of UTC (the Line Islands, UTC+14): no signed date is later than the day it is there. */
const LATEST_OFFSET_HOURS = 14;

const DIGITS = /^[0-9]+$/;
const ISO_DAY = /^([0-9]{4})-([0-9]{2})-([0-9]{2})$/;

/** The box's text with spaces, tabs and line ends taken off both ends, and nothing else touched (the door strips the same six). */
function trimmed(value: unknown): string {
  return `${value ?? ""}`.replace(/^[ \t\n\r\f\v]+|[ \t\n\r\f\v]+$/g, "");
}

export function isLeapYear(year: number): boolean {
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
}

function daysInMonth(year: number, month: number): number {
  return [31, isLeapYear(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1];
}

/** Is this a day on the calendar? Years 1 to 9999, the range both languages' dates hold. */
function isDay(year: number, month: number, day: number): boolean {
  return year >= 1 && year <= 9999 && month >= 1 && month <= 12 && day >= 1 && day <= daysInMonth(year, month);
}

/** Negative when `a` is before `b`, 0 on the same day, positive after. */
export function compareDays(a: CalendarDay, b: CalendarDay): number {
  return a.year - b.year || a.month - b.month || a.day - b.day;
}

/** "YYYY-MM-DD" (the date box's own value) as a day, or null for anything else. */
export function calendarDayOf(text: unknown): CalendarDay | null {
  const found = ISO_DAY.exec(trimmed(text));
  if (!found) return null;
  const [year, month, day] = [Number(found[1]), Number(found[2]), Number(found[3])];
  return isDay(year, month, day) ? { year, month, day } : null;
}

/** The day as the date box writes it, "YYYY-MM-DD". */
export function isoOf(day: CalendarDay): string {
  const pad = (n: number, width: number) => `${n}`.padStart(width, "0");
  return `${pad(day.year, 4)}-${pad(day.month, 2)}-${pad(day.day, 2)}`;
}

/** Today on this phone's own clock and zone: the day where the client is standing. */
export function todayHere(now: Date = new Date()): CalendarDay {
  return { year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() };
}

/** The latest day it is anywhere on Earth at this instant (UTC+14). The same instant reads the same day at the door. */
export function latestDayOnEarth(now: Date = new Date()): CalendarDay {
  const ahead = new Date(now.getTime() + LATEST_OFFSET_HOURS * 3600 * 1000);
  return { year: ahead.getUTCFullYear(), month: ahead.getUTCMonth() + 1, day: ahead.getUTCDate() };
}

/** THE DAY OF SIGNING: the signed date's box when it holds a day no later than the latest day on Earth now, else `today`. */
export function signingDayOf(signedDate: unknown, today: CalendarDay, now: Date = new Date()): CalendarDay {
  const signed = calendarDayOf(signedDate);
  return signed && compareDays(signed, latestDayOnEarth(now)) <= 0 ? signed : today;
}

/** The birth date the three boxes say, read on the day of signing `on`, or null when they say no day that has happened. */
export function birthDayOf(year: unknown, month: unknown, day: unknown, on: CalendarDay): CalendarDay | null {
  const parts = [trimmed(year), trimmed(month), trimmed(day)];
  if (!parts.every((part) => DIGITS.test(part))) return null;
  const [y, m, d] = parts.map(Number);
  // two digits typed for four: this century unless that day has not come yet
  for (const full of y < 100 ? [2000 + y, 1900 + y] : [y]) {
    const born = { year: full, month: m, day: d };
    if (isDay(full, m, d) && compareDays(born, on) <= 0) return born;
  }
  return null;
}

/** THE AGE ON THE DAY OF SIGNING, in whole years, or null when the boxes hold no birth date that has happened by then. */
export function ageOn(year: unknown, month: unknown, day: unknown, on: CalendarDay): number | null {
  const born = birthDayOf(year, month, day, on);
  if (!born) return null;
  // the birthday in the year of signing: a February 29 is February 28 in a common year
  const birthdayDay = born.month === 2 && born.day === 29 && !isLeapYear(on.year) ? 28 : born.day;
  const reached = on.month > born.month || (on.month === born.month && on.day >= birthdayDay);
  return on.year - born.year - (reached ? 0 : 1);
}
